import React from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import { ProtectedLayout } from './layouts';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { SubscriptionProtectedRoute } from '@/components/routing/SubscriptionProtectedRoute';
import { MobileRouteRedirect, DesktopRouteRedirect } from '@/components/routing/MobileRouteRedirect';

// Core pages (eagerly loaded for fast initial navigation)
import Calculator from '@/pages/Calculator';
import MobileCalculator from '@/pages/MobileCalculator';
import Dashboard from '@/pages/Dashboard';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';

// Lazy-loaded pages (heavy or infrequently accessed)
const MortgageReport = React.lazy(() => import('@/pages/MortgageReport'));
const BuyingPowerReport = React.lazy(() => import('@/pages/BuyingPowerReport'));
const CalculatorReport = React.lazy(() => import('@/pages/CalculatorReport'));
const UnifiedCalculatorReport = React.lazy(() => import('@/pages/UnifiedCalculatorReport'));
// InvestorAnalyticsReport moved to publicRoutes (freemium)
const ChatbotReport = React.lazy(() => import('@/pages/ChatbotReport'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const CLARAAnalytics = React.lazy(() => import('@/pages/CLARAAnalytics'));
const UnifiedBuyingPowerCalculator = React.lazy(() => import('@/pages/UnifiedBuyingPowerCalculator'));
const HomeQuest = React.lazy(() => import('@/pages/HomeQuest'));
const MobileHomeQuest = React.lazy(() => import('@/pages/MobileHomeQuest'));
const BuyingPowerEnhanced = React.lazy(() => import('@/pages/BuyingPowerEnhanced'));
const MobileSmartBuyAnalyzer = React.lazy(() => import('@/pages/MobileSmartBuyAnalyzer'));
const MobileSmartBuyComparison = React.lazy(() => import('@/pages/MobileSmartBuyComparison'));
const SmartBuyAnalyzer = React.lazy(() => import('@/pages/SmartBuyAnalyzer'));
// Investor pages (InvestorAnalytics, MobileInvestorAnalytics, MobileUnifiedCalculator)
// moved to publicRoutes (freemium - publicly accessible)

const Portfolio = React.lazy(() => import('@/pages/Portfolio'));
const PropertyTaxDatabase = React.lazy(() => import('@/pages/PropertyTaxDatabase'));
const CommunityIntelligence = React.lazy(() => import('@/pages/CommunityIntelligence'));
const HomebuyerChecklist = React.lazy(() => import('@/pages/HomebuyerChecklist'));
const Compare = React.lazy(() => import('@/pages/Compare'));
const ChatBot = React.lazy(() => import('@/pages/ChatBot'));
const MobileClaraGo = React.lazy(() => import('@/pages/MobileClaraGo'));
const MobileAnalytics = React.lazy(() => import('@/pages/MobileAnalytics'));
const ScrapingDashboard = React.lazy(() => import('@/pages/ScrapingDashboard'));
const ProductionScrapingDashboard = React.lazy(() => import('@/pages/ProductionScrapingDashboard'));
const Admin = React.lazy(() => import('@/pages/Admin'));
const MarketingStrategy = React.lazy(() => import('@/pages/admin/MarketingStrategy'));
const BillingPage = React.lazy(() => import('@/pages/BillingPage'));
const BillingSuccess = React.lazy(() => import('@/pages/billing/Success'));
const ContractReadyForm = React.lazy(() => import('@/pages/ContractReadyForm'));
const UserManual = React.lazy(() => import('@/pages/UserManual'));
const FloorPlans = React.lazy(() => import('@/pages/FloorPlans'));
const Lots = React.lazy(() => import('@/pages/Lots'));
const PlatExplorer = React.lazy(() => import('@/pages/PlatExplorer'));
const InventoryDashboard = React.lazy(() => import('@/pages/InventoryDashboard'));
const Widgets = React.lazy(() => import('@/pages/admin/Widgets'));
const Organizations = React.lazy(() => import('@/pages/admin/Organizations'));
const PatentTrackerPage = React.lazy(() => import('@/pages/admin/PatentTrackerPage'));
const DatabaseMaintenance = React.lazy(() => import('@/pages/admin/DatabaseMaintenance'));
const SecurityMonitoring = React.lazy(() => import('@/pages/admin/SecurityMonitoring'));
const SystemStatusDashboard = React.lazy(() => import('@/pages/admin/SystemStatusDashboard'));
const DataAggregator = React.lazy(() => import('@/pages/admin/DataAggregator'));
const HomeComparisonEmail = React.lazy(() => import('@/pages/HomeComparisonEmail'));
const LeadsDashboard = React.lazy(() => import('@/pages/LeadsDashboard'));
const ReversePitiCalculator = React.lazy(() => import('@/pages/ReversePitiCalculator'));
const MobileReversePitiCalculator = React.lazy(() => import('@/pages/MobileReversePitiCalculator'));
const SystemDiagnostic = React.lazy(() => import('@/pages/SystemDiagnostic'));
const MarketIntelligence = React.lazy(() => import('@/pages/MarketIntelligence'));
const DealAnalyzer = React.lazy(() => import('@/pages/DealAnalyzer'));

export const protectedRoutes: RouteObject[] = [
  {
    path: '/calculator',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/calculator-mobile">
          <Calculator />
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/reverse-piti-calculator',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/mobile-reverse-piti-calculator">
          <ReversePitiCalculator />
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-reverse-piti-calculator',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/reverse-piti-calculator">
          <MobileReversePitiCalculator />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/calculator-mobile',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/calculator">
          <MobileCalculator />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/contract-ready-form',
    element: (
      <ProtectedLayout>
        <ContractReadyForm />
      </ProtectedLayout>
    ),
  },
  {
    path: '/mortgage-report',
    element: (
      <ProtectedLayout>
        <MortgageReport />
      </ProtectedLayout>
    ),
  },
  {
    path: '/buying-power-report',
    element: (
      <ProtectedLayout>
        <BuyingPowerReport />
      </ProtectedLayout>
    ),
  },
  {
    path: '/calculator-report',
    element: (
      <ProtectedLayout>
        <CalculatorReport />
      </ProtectedLayout>
    ),
  },
  {
    path: '/unified-calculator-report',
    element: (
      <ProtectedLayout>
        <UnifiedCalculatorReport />
      </ProtectedLayout>
    ),
  },
  // /investor-analytics-report is now public — see publicRoutes.tsx.

  {
    path: '/chatbot-report',
    element: (
      <ProtectedLayout>
        <ChatbotReport />
      </ProtectedLayout>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedLayout>
        <Dashboard />
      </ProtectedLayout>
    ),
  },
  {
    path: '/analytics',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/mobile-analytics">
          <Analytics />
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/unified-calculator',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="pro" 
          featureName="Unified Calculator"
          featureDescription="Advanced unified calculation features with unlimited usage"
        >
          <UnifiedBuyingPowerCalculator />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/homequest',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/mobile-homequest">
          <HomeQuest />
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-homequest',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/homequest">
          <MobileHomeQuest />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/buying-power-enhanced',
    element: (
      <ProtectedLayout>
        <BuyingPowerEnhanced />
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-smartbuy-analyzer',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/smartbuy-analyzer">
          <MobileSmartBuyAnalyzer />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-smartbuy-comparison',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="pro" 
          featureName="Mobile SmartBuy Comparison"
          featureDescription="Mobile-optimized scenario comparison tool with swipe navigation"
        >
          <MobileSmartBuyComparison />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/buying-power',
    element: <Navigate to="/smartbuy-analyzer" replace />,
  },
  {
    path: '/smartbuy-analyzer',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/mobile-smartbuy-analyzer">
          <SubscriptionProtectedRoute 
            requiredTier="pro" 
            featureName="SmartBuy Analyzer"
            featureDescription="Advanced property analysis and buying power calculations"
          >
            <SmartBuyAnalyzer />
          </SubscriptionProtectedRoute>
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  // Investor analytics, mobile investor analytics, and mobile unified calculator
  // are now publicly accessible — see publicRoutes.tsx.
  {
    path: '/market-intelligence',
    element: (
      <ProtectedLayout>
        <MarketIntelligence />
      </ProtectedLayout>
    ),
  },
  {
    path: '/deal-analyzer',
    element: (
      <ProtectedLayout>
        <DealAnalyzer />
      </ProtectedLayout>
    ),
  },

  {
    path: '/portfolio',
    element: (
      <ProtectedLayout>
        <Portfolio />
      </ProtectedLayout>
    ),
  },
  {
    path: '/property-tax-database',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="agent_pro" 
          featureName="Property Tax Database"
          featureDescription="Access to comprehensive property tax data and calculations"
        >
          <PropertyTaxDatabase />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/community-intelligence',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="agent_pro" 
          featureName="Community Intelligence"
          featureDescription="Advanced community data and market intelligence features"
        >
          <CommunityIntelligence />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/checklist',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="agent_pro" 
          featureName="Homebuyer Checklist"
          featureDescription="Comprehensive homebuying checklists and guidance tools"
        >
          <HomebuyerChecklist />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/compare',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="agent_pro" 
          featureName="Property Comparison"
          featureDescription="Advanced property comparison and analysis tools"
        >
          <Compare />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/chatbot',
    element: (
      <ProtectedLayout>
        <MobileRouteRedirect mobileRoute="/mobile-clara-go">
          <SubscriptionProtectedRoute 
            requiredTier="agent_pro" 
            featureName="AI ChatBot"
            featureDescription="AI-powered real estate assistant for advanced guidance"
          >
            <ChatBot />
          </SubscriptionProtectedRoute>
        </MobileRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-clara-go',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/chatbot">
          <MobileClaraGo />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/mobile-analytics',
    element: (
      <ProtectedLayout>
        <DesktopRouteRedirect desktopRoute="/analytics">
          <MobileAnalytics />
        </DesktopRouteRedirect>
      </ProtectedLayout>
    ),
  },
  {
    path: '/clara',
    element: <Navigate to="/chatbot" replace />,
  },
  {
    path: '/clara-analytics',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="agent_pro" 
          featureName="CLARA Analytics"
          featureDescription="Advanced conversation analytics and insights dashboard"
        >
          <CLARAAnalytics />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/scraping-dashboard',
    element: (
      <ProtectedLayout>
        <ScrapingDashboard />
      </ProtectedLayout>
    ),
  },
  {
    path: '/production-scraping-dashboard',
    element: (
      <ProtectedLayout>
        <ProductionScrapingDashboard />
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin',
    element: (
      <ProtectedLayout>
        <SubscriptionProtectedRoute 
          requiredTier="enterprise" 
          featureName="Admin Dashboard"
          featureDescription="Enterprise-level administration and management tools"
        >
          <Admin />
        </SubscriptionProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/marketing-strategy',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <MarketingStrategy />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/widgets',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <Widgets />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/organizations',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <Organizations />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/patent-tracker',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <PatentTrackerPage />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/database-maintenance',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <DatabaseMaintenance />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/security-monitoring',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <SecurityMonitoring />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/system-status',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <SystemStatusDashboard />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/admin/data-aggregator',
    element: (
      <ProtectedLayout>
        <ProtectedRoute requiredRole={['admin', 'owner']}>
          <DataAggregator />
        </ProtectedRoute>
      </ProtectedLayout>
    ),
  },
  {
    path: '/profile',
    element: (
      <ProtectedLayout>
        <Profile />
      </ProtectedLayout>
    ),
  },
  {
    path: '/billing',
    element: (
      <ProtectedLayout>
        <BillingPage />
      </ProtectedLayout>
    ),
  },
  {
    path: '/billing/success',
    element: (
      <ProtectedLayout>
        <BillingSuccess />
      </ProtectedLayout>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedLayout>
        <Settings />
      </ProtectedLayout>
    ),
  },
  {
    path: '/pricing',
    element: <Navigate to="/billing" replace />,
  },
  {
    path: '/user-manual',
    element: (
      <ProtectedLayout>
        <UserManual />
      </ProtectedLayout>
    ),
  },
  {
    path: '/floor-plans',
    element: (
      <ProtectedLayout>
        <FloorPlans />
      </ProtectedLayout>
    ),
  },
  {
    path: '/lots',
    element: (
      <ProtectedLayout>
        <Lots />
      </ProtectedLayout>
    ),
  },
  {
    path: '/plat-explorer',
    element: (
      <ProtectedLayout>
        <PlatExplorer />
      </ProtectedLayout>
    ),
  },
  {
    path: '/inventory',
    element: (
      <ProtectedLayout>
        <InventoryDashboard />
      </ProtectedLayout>
    ),
  },
  {
    path: '/home-comparison-email',
    element: (
      <ProtectedLayout>
        <HomeComparisonEmail />
      </ProtectedLayout>
    ),
  },
  {
    path: '/leads',
    element: (
      <ProtectedLayout>
        <LeadsDashboard />
      </ProtectedLayout>
    ),
  },
  {
    path: '/system-diagnostic',
    element: (
      <ProtectedLayout>
        <SystemDiagnostic />
      </ProtectedLayout>
    ),
  },
];